import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Container,
  Image,
  Spinner,
  Stack,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Grid,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { ChevronRightIcon, ArrowBackIcon } from "@chakra-ui/icons";
import SectionHeading from "../components/SectionHeading";
import { GET } from "../utilities/ApiProvider";
import { baseUrl } from "../utilities/customUrls";
import { useLocation } from "react-router-dom";

export default function AlbumDetail() {
  const location = useLocation();
  const { albumId } = useParams();
  const navigate = useNavigate();

  const { albumName } = location.state || {};

  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const getAlbumImages = async (albumId) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await GET(`${baseUrl}/Album/GetAlbumImages/${albumId}`, {
        authorization: `Basic ${btoa("admin:admin123")}`,
      });

      if (response?.Data) {
        setImages(response.Data);
      } else {
        setError("No images found in this album");
      }
    } catch (err) {
      console.error("Error fetching album images:", err);
      setError("Failed to load album images. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (albumId) {
      getAlbumImages(albumId);
    }
  }, [albumId]);

  const handleBackClick = () => {
    navigate("/media/photo-gallery");
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    onOpen();
  };

  return (
    <Stack py="80px">
      <Container maxW="6xl">
        {/* Back Button */}
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={handleBackClick}
          mb={4}
        >
          Back to Albums
        </Button>

        {/* Breadcrumb */}
        <Breadcrumb
          spacing="8px"
          separator={<ChevronRightIcon color="gray.500" />}
          mb={6}
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/">
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/media/photo-gallery">
              Photo Gallery
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>{albumName || `Album ${albumId}`}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Album Title */}
        <Stack pb={8}>
          <SectionHeading
            width="100%"
            smallHeading={albumName || `Album ${albumId}`}
            mainHeading=""
            marginPosition="auto"
            textAlignment="center"
          />
        </Stack>

        {/* Error */}
        {error && (
          <Text color="red.500" textAlign="center" mb={4}>
            {error}
          </Text>
        )}

        {/* Images */}
        {isLoading ? (
          <Stack align="center" justify="center" minH="200px">
            <Spinner size="xl" />
          </Stack>
        ) : images.length === 0 ? (
          <Stack align="center" justify="center" minH="200px">
            <Text fontSize="lg" color="gray.500">
              No images in this album
            </Text>
          </Stack>
        ) : (
          <Grid
            templateColumns="repeat(auto-fit, minmax(220px, 1fr))"
            gap={6}
            justifyItems="center"
          >
            {images.map((image) => (
              <Box
                key={image.ImageId}
                w="100%"
                maxW="260px"
                cursor="pointer"
                transition="all 0.25s ease"
                _hover={{ transform: "scale(1.05)", boxShadow: "xl" }}
                onClick={() => handleImageClick(image)}
              >
                <Box
                  borderRadius="md"
                  overflow="hidden"
                  boxShadow="md"
                  position="relative"
                >
                  <Image
                    src={image.Image || image.ImageUrl}
                    alt={image.Caption || "Album image"}
                    w="100%"
                    h="200px"
                    objectFit="cover"
                    loading="lazy"
                  />

                  {image.Caption && (
                    <Box
                      position="absolute"
                      bottom={0}
                      left={0}
                      right={0}
                      bg="rgba(0,0,0,0.65)"
                      color="white"
                      p={2}
                      fontSize="sm"
                    >
                      <Text noOfLines={2}>{image.Caption}</Text>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Grid>
        )}
      </Container>

      {/* Image Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="black">
          <ModalCloseButton
            color="white"
            bg="blackAlpha.600"
            _hover={{ bg: "blackAlpha.800" }}
          />
          <ModalBody p={0}>
            {selectedImage && (
              <Box>
                <Image
                  src={selectedImage.Image}
                  alt="Full size image"
                  w="100%"
                  maxH="90vh"
                  objectFit="contain"
                />
                {/* {selectedImage.Caption && (
                  <Box p={4} bg="gray.50">
                    <Text fontWeight="semibold">{selectedImage.Caption}</Text>
                  </Box>
                )} */}
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Stack>
  );
}
